import { useCallback, useEffect, useRef, useState } from "react";
import { Hand, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLiveRoomEvents } from "@/hooks/useLiveRoomWS";

/**
 * Part 6 — always-visible "Request to Co-host" for viewers.
 * idle → pending (tap again = cancel) → declined (60 s cooldown) → on stage
 * (component unmounts because role flips to cohost). Server enforces every
 * transition; this only mirrors /cohost/my-status.
 */
export default function LiveRoomRequestCohostButton({ streamId, userId }) {
  const [status, setStatus] = useState(null); // pending | denied | null
  const [retryIn, setRetryIn] = useState(0);
  const [boxes, setBoxes] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const sync = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/${streamId}/cohost/my-status`);
      setStatus(data?.request_status === "pending" ? "pending" : data?.denied_retry_in > 0 ? "denied" : null);
      setRetryIn(data?.denied_retry_in || 0);
      setBoxes(typeof data?.boxes_available === "number" ? data.boxes_available : null);
    } catch { /* transient */ }
  }, [streamId]);

  useEffect(() => { sync(); }, [sync]);
  useLiveRoomEvents((m) => {
    if ((m?.channel === "cohost:decline" || m?.channel === "cohost:accept") && m?.requester_id === userId) sync();
    if (m?.channel === "stage:changed" || m?.channel === "cohost:left") sync();
  });

  useEffect(() => {
    clearInterval(timer.current);
    if (retryIn > 0) {
      timer.current = setInterval(() => setRetryIn((s) => {
        if (s <= 1) { clearInterval(timer.current); setStatus(null); return 0; }
        return s - 1;
      }), 1000);
    }
    return () => clearInterval(timer.current);
  }, [retryIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (status === "pending") {
        await api.post(`/live/${streamId}/cohost/request/cancel`);
        setStatus(null);
        toast("Request withdrawn.");
      } else {
        await api.post(`/live/${streamId}/cohost/request`);
        setStatus("pending");
        toast.success("Request sent — the host will bring you up when a box is free.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d || "Could not send request.");
      sync();
    } finally {
      setBusy(false);
    }
  };

  const denied = status === "denied" && retryIn > 0;
  const label = status === "pending" ? "Requested" : denied ? `Declined · ${retryIn}s` : "Request to Co-host";
  return (
    <button
      type="button"
      data-testid="request-cohost-button"
      data-state={status === "pending" ? "pending" : denied ? "denied" : "idle"}
      onClick={act}
      disabled={busy || denied}
      aria-label={status === "pending" ? "Cancel co-host request" : "Request to co-host"}
      title={boxes === 0 ? "Stage is full right now — the host can still approve you when a box frees up" : undefined}
      className={`flex items-center gap-1.5 h-10 md:h-9 rounded-full px-3 md:pl-2.5 md:pr-3 text-[11px] font-semibold backdrop-blur-md border transition-colors disabled:opacity-70 ${
        status === "pending"
          ? "bg-amber-400/90 text-black border-amber-300"
          : denied
            ? "bg-black/60 text-zinc-300 border-white/10"
            : "bg-black/60 text-white border-white/15 hover:border-amber-300/60"
      }`}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : status === "pending" ? <X size={13} /> : <Hand size={13} />}
      <span className="whitespace-nowrap md:hidden">{status === "pending" ? "Requested" : denied ? `${retryIn}s` : "Co-host"}</span>
      <span className="whitespace-nowrap hidden md:inline">{label}</span>
      {boxes === 0 && status !== "pending" && !denied && <span className="text-[9px] uppercase tracking-wider text-amber-300">full</span>}
    </button>
  );
}
